import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { AlertCircle, Package, AlertTriangle } from 'lucide-react';

/**
 * PromotionCardsGridBlock - Grille de cartes promotionnelles
 * 
 * VERSION: 8.0 - PRODUCTION READY (Clean)
 * 
 * MODES SUPPORTÉS:
 * ✅ 'manual'      - Cartes CMS statiques définies par l'admin
 * ✅ 'offers_auto' - Offres dynamiques depuis /api/offers/public/active
 * 
 * MODES NON SUPPORTÉS:
 * ❌ Tout autre mode génère une erreur explicite
 * 
 * CARACTÉRISTIQUES:
 * - Un seul appel API au montage
 * - Pas de re-render infini
 * - Pas de fallback implicite
 * - Responsive: 1 col mobile, 2 cols tablette, 3 cols desktop
 */

// Modes CMS valides - Liste exhaustive
const VALID_MODES = ['manual', 'offers_auto'];

const PromotionCardsGridBlock = ({ block }) => {
  // ============================================================
  // EXTRACTION & VALIDATION DES PROPS
  // ============================================================
  const content = block.content || {};
  const {
    cards = [],
    corner_radius = 'lg',
    card_spacing = 'md',
    source_mode = 'manual',
    offers_limit = 20,
    offers_partner_id = null
  } = content;

  // Validation stricte du mode - Pas de fallback implicite
  const isValidMode = VALID_MODES.includes(source_mode);

  // ============================================================
  // STATE
  // ============================================================
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Refs pour éviter les appels multiples et les memory leaks
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  // ============================================================
  // FETCH FUNCTION (mode offers_auto uniquement)
  // ============================================================
  const fetchOffers = useCallback(async (limit, partnerId) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `/offers/public/active?limit=${limit || 20}`;
      if (partnerId) {
        url += `&partner_id=${partnerId}`;
      }
      const response = await api.get(url);
      
      if (mountedRef.current) {
        const data = response.data || [];
        // Dédupliquer par ID
        const uniqueOffers = Array.from(new Map(data.map(o => [o.id, o])).values());
        setOffers(uniqueOffers);
        setHasLoaded(true);
      }
    } catch (err) {
      console.error('[PromotionCardsGrid] Erreur chargement offres:', err);
      if (mountedRef.current) {
        setError('Impossible de charger les offres');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
      fetchingRef.current = false;
    }
  }, []);

  // ============================================================
  // EFFECT: Chargement initial (UNE SEULE FOIS)
  // ============================================================
  useEffect(() => {
    mountedRef.current = true;
    
    // Reset
    setHasLoaded(false);
    setOffers([]);
    setError(null);
    fetchingRef.current = false;

    // Charger les offres uniquement en mode offers_auto
    if (source_mode === 'offers_auto') {
      fetchOffers(offers_limit, offers_partner_id);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [source_mode, offers_limit, offers_partner_id, fetchOffers]);

  // ============================================================
  // FILTRAGE DES CARTES MANUELLES (mode manual)
  // ============================================================
  const activeCards = useMemo(() => {
    const now = new Date();
    return cards.filter(card => {
      if (card.is_active === false) return false;
      if (card.startDate && new Date(card.startDate) > now) return false;
      if (card.endDate && new Date(card.endDate) < now) return false;
      return true;
    });
  }, [cards]);

  // ============================================================
  // MAPPING OFFERS → CARDS FORMAT
  // ============================================================
  const mappedOffers = useMemo(() => {
    return offers.map(offer => ({
      id: offer.id,
      image: offer.image_url,
      title: offer.title,
      description: offer.description,
      link: `/offre/${offer.id}`,
      badge_text: offer.badge_text,
      badge_color: offer.badge_color || '#ef4444',
      discount_percent: offer.discount_percent,
      partner_name: offer.partner_name,
      type: offer.type,
      startDate: offer.start_date,
      endDate: offer.end_date
    }));
  }, [offers]);

  // ============================================================
  // DÉTERMINER LES ITEMS À AFFICHER
  // ============================================================
  const displayItems = source_mode === 'offers_auto' ? mappedOffers : activeCards;

  // ============================================================
  // RENDER: Mode invalide - Erreur explicite
  // ============================================================
  if (!isValidMode) {
    return (
      <div 
        className="promotion-cards-grid-block bg-red-50 border-2 border-red-300 rounded-lg p-6"
        data-testid="promotion-cards-grid-block"
        data-block-type="promotion_cards_grid"
        data-error="invalid-mode"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">
              Mode CMS non supporté: "{source_mode}"
            </h4>
            <p className="text-sm text-red-600 mt-1">
              Modes valides: <code className="bg-red-100 px-1 rounded">manual</code>, <code className="bg-red-100 px-1 rounded">offers_auto</code>
            </p>
            <p className="text-xs text-red-500 mt-2">
              Veuillez éditer ce bloc CMS et sélectionner un mode valide.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER: États de chargement
  // ============================================================
  
  // Loading state (mode offers_auto)
  if (loading && !hasLoaded && source_mode === 'offers_auto') {
    return (
      <div 
        className="promotion-cards-grid-block text-center py-12"
        data-testid="promotion-cards-grid-block"
        data-block-type="promotion_cards_grid"
      >
        <div className="inline-flex items-center gap-3 text-muted-foreground">
          <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Chargement des offres...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error && source_mode === 'offers_auto') {
    return (
      <div 
        className="promotion-cards-grid-block bg-red-50 border border-red-200 rounded-lg p-6 text-center"
        data-testid="promotion-cards-grid-block"
        data-block-type="promotion_cards_grid"
      >
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-700">{error}</p>
        <p className="text-sm text-red-500 mt-1">Veuillez réessayer plus tard</p>
      </div>
    );
  }

  // Empty state
  if (displayItems.length === 0) {
    if (source_mode === 'offers_auto') {
      return (
        <div 
          className="promotion-cards-grid-block bg-gray-50 border border-gray-200 rounded-lg p-8 text-center"
          data-testid="promotion-cards-grid-block"
          data-block-type="promotion_cards_grid"
        >
          <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Aucune offre disponible actuellement</p>
          <p className="text-sm text-gray-400 mt-1">
            De nouvelles offres seront bientôt disponibles
          </p>
        </div>
      );
    }
    
    // Mode manual sans cartes (dev only)
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center my-4">
          <p className="text-sm text-yellow-700">
            ⚠️ Bloc Promotion Cards Grid (mode manual) : aucune carte active
          </p>
        </div>
      );
    }
    return null;
  }

  // ============================================================
  // RENDER: Grille de cartes
  // ============================================================
  const radiusClasses = {
    sm: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl'
  };

  const spacingClasses = {
    sm: 'gap-3',
    md: 'gap-6',
    lg: 'gap-8'
  };

  const radiusClass = radiusClasses[corner_radius] || radiusClasses.lg;
  const spacingClass = spacingClasses[card_spacing] || spacingClasses.md;

  return (
    <div 
      className="promotion-cards-grid-block"
      data-testid="promotion-cards-grid-block"
      data-block-type="promotion_cards_grid"
      data-mode={source_mode}
      style={{
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${spacingClass}`}>
        {displayItems.map((card) => (
          <PromotionCard
            key={card.id}
            card={card}
            radiusClass={radiusClass}
            isOfferMode={source_mode === 'offers_auto'}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * PromotionCard - Carte promotionnelle individuelle
 * Composant pur mémorisé pour éviter les re-renders inutiles
 */
const PromotionCard = React.memo(({ card, radiusClass, isOfferMode }) => {
  const {
    image,
    title,
    description,
    link,
    startDate,
    endDate,
    badge_text,
    badge_color,
    discount_percent,
    partner_name,
    type
  } = card;

  // Image de fallback
  const displayImage = image || 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=800&q=80';

  // Icône selon le type d'offre
  const typeIcons = {
    remise: '💰',
    franco: '🚚',
    goodies: '🎁',
    mixte: '✨'
  };
  const typeIcon = type ? typeIcons[type] || '🏷️' : null;

  // 🔥 BADGE "EXPIRE BIENTÔT" - Offres expirant dans 7 jours ou moins
  const isExpiringSoon = useMemo(() => {
    if (!endDate) return false;
    const now = new Date();
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return false;
    const diffTime = end - now;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    // Afficher le badge si l'offre expire dans 7 jours ou moins ET n'est pas encore expirée
    return diffDays >= 0 && diffDays <= 7;
  }, [endDate]);

  // Formater une date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date)) return null;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Texte des dates
  const dateText = useMemo(() => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (start && end) return `${start} → ${end}`;
    if (start) return `Dès le ${start}`;
    if (end) return `Jusqu'au ${end}`;
    return null;
  }, [startDate, endDate]);

  const isExternal = link && (link.startsWith('http://') || link.startsWith('https://'));
  const isClickable = !!link && link !== '#';

  // Contenu de la carte
  const cardContent = (
    <div
      className={`bg-white ${radiusClass} overflow-hidden shadow-md ${
        isClickable 
          ? 'hover:shadow-xl hover:scale-[1.02] cursor-pointer transition-all duration-300 ease-in-out focus-within:ring-2 focus-within:ring-primary' 
          : 'hover:shadow-lg transition-shadow duration-300'
      }`}
      data-testid="promotion-card"
    >
      {/* Image - Content Fit System: contain pour éviter crop */}
      <div className="relative h-44 overflow-hidden bg-white border-b border-black/[0.06] flex items-center justify-center">
        <img
          src={displayImage}
          alt={title || 'Promotion'}
          className="max-w-full max-h-full object-contain p-2"
          loading="lazy"
          data-testid="promotion-card-image"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=800&q=80';
          }}
        />
        
        {/* 🔥 Badge "EXPIRE BIENTÔT" - coin supérieur gauche */}
        {isExpiringSoon && (
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-full text-white text-xs font-bold shadow-lg animate-pulse"
            style={{ backgroundColor: '#ef4444', zIndex: 10 }}
            data-testid="promotion-card-expiring-badge"
          >
            🔥 Expire bientôt
          </div>
        )}
        
        {/* Badge principal - coin supérieur droit */}
        {(badge_text || discount_percent) && (
          <div 
            className="absolute top-3 right-3 px-3 py-1 rounded-full text-white text-sm font-bold shadow-lg"
            style={{ backgroundColor: badge_color || '#ef4444' }}
            data-testid="promotion-card-badge"
          >
            {badge_text || (discount_percent ? `-${discount_percent}%` : '')}
          </div>
        )}
      </div>

      {/* Contenu */}
      <div className="p-4">
        {title && (
          <h3
            className="text-lg font-heading font-bold text-gray-900 mb-2 line-clamp-2"
            data-testid="promotion-card-title"
          >
            {title}
          </h3>
        )}

        {isOfferMode && partner_name && (
          <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            {partner_name}
          </p>
        )}

        {dateText && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <span>📅</span>
              <span>{dateText}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Wrapper cliquable
  if (isClickable) {
    if (isExternal) {
      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Voir l'offre : ${title || 'Sans titre'}`}
          className="block focus:outline-none"
        >
          {cardContent}
        </a>
      );
    }
    return (
      <Link
        to={link}
        aria-label={`Voir l'offre : ${title || 'Sans titre'}`}
        className="block focus:outline-none"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
});

PromotionCard.displayName = 'PromotionCard';

export default PromotionCardsGridBlock;
